import React from 'react';

// Components
import FormattedMessageV2 from '@/views/components/i18n/FormattedMessageV2';
import { ControlItem, Tabs as TabsV2 } from '@/views/components/navigation';
import { TabFields } from './components/TabFields';

// Libraries
import { useSelector } from 'react-redux';

// Types
import HistoricalScenario from '@/types/historical-scenario';
import Locale from '@/types/locale';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { LOCALE } from '@/utils/i18n';

interface TabsProps {
  i18nBasePath: string;
  infoFields: Partial<HistoricalScenario>;
  loading: boolean;
  onSelectTab: (tab: Locale) => void;
  onUpdateFields: (tabsFields: Partial<HistoricalScenario>) => void;
}

export const Tabs: React.FC<TabsProps> = ({ i18nBasePath, infoFields, loading, onSelectTab, onUpdateFields }) => {
  const appLocales = useSelector((state: ReduxState) => state.auth.user?.appConfig.appLocales || [LOCALE.EN_CA]);
  const [selectedTab, setSelectedTab] = React.useState<Locale>(LOCALE.EN_CA);

  const tabsOptions = appLocales.map(appLocale => ({
    value: appLocale,
    i18nPath: `${i18nBasePath}.Language.${appLocale}`,
  }));

  const handleOnChangeField = (field: keyof HistoricalScenario, value: string) => {
    const updatedTabsFields = {
      ...infoFields,
      [field]: {
        ...(typeof infoFields[field] === 'object' && infoFields[field] !== null ? infoFields[field] : {}),
        [selectedTab]: value,
      },
    };
    onUpdateFields(updatedTabsFields);
  };

  const onContentTabChange = (tab: Locale) => {
    setSelectedTab(tab);
    onSelectTab(tab);
  };

  return (
    <>
      <div className="tabs-container">
        <TabsV2 className="tabs-v2" disabled={loading} onChange={onContentTabChange} selected={selectedTab}>
          {tabsOptions.map(option => (
            <ControlItem key={option.value} value={option.value}>
              <FormattedMessageV2 toLocalize={option.i18nPath} />
            </ControlItem>
          ))}
        </TabsV2>
      </div>

      {appLocales.map(
        key =>
          selectedTab === key && (
            <TabFields
              i18nBasePath={i18nBasePath}
              key={`upsertForm-tabFields-${key}`}
              loading={loading}
              locale={key || LOCALE.EN_CA}
              localeDescription={infoFields.description?.[key]}
              localeName={infoFields.name?.[key]}
              localeShortDescription={infoFields.shortDescription?.[key]}
              onChangeField={handleOnChangeField}
            />
          ),
      )}
    </>
  );
};
